"use client";

import * as React from "react";
import { Bookmark } from "lucide-react";

import { CORE_COLORS } from "@/lib/theme/core";
import { useToggleToast } from "@/lib/toast";

/**
 * The standalone 收藏 (bookmark) toggle.
 *
 * Several cards rendered a bare `<button aria-label="收藏">` with NO onClick —
 * the icon was there, the button did nothing. QA filed it repeatedly ("功能不
 * 完整，無法加入書籤", "缺乏功能", "1. 無法加入書籤 2. 而且懸停時也沒有變成手指
 * 圖示"). This is the single implementation for those call sites; cards that
 * already render the full meta row keep using `MetaRow`, which has its own copy
 * of the same behaviour.
 *
 * `preventDefault` + `stopPropagation` matter: these buttons often sit inside a
 * card-wide `<a>`, and without them a click navigates to the article instead of
 * bookmarking it — the "加入書籤會直接挑轉去到嗰一片文章 做法错误" report.
 *
 * State is local and resets on navigation. Persisting a bookmark needs an API
 * that does not exist yet in this build (all data comes from static fixtures).
 */
/**
 * Confirmation for a bookmark toggle. QA asked for a toast on every toggle so
 * the action is acknowledged even when the icon is off-screen or the change is
 * easy to miss.
 */
/**
 * Fill for the SAVED state — lighter than the stroke, so the icon stays
 * readable as a bookmark instead of becoming a solid block of the stroke
 * colour. QA: "when the bookmark icon button is filled, fill it with a colour
 * lighter than the current stroke colour."
 */
const SAVED_FILL = "#8ccbb4";

export function BookmarkButton({
  size = 20,
  strokeWidth = 1.4,
  color = CORE_COLORS.brandGreen,
  fillColor = SAVED_FILL,
  initialBookmarked = false,
  onChange,
  className = "shrink-0",
}: {
  /** Bumped 16 → 20: QA asked for a slightly larger icon button. */
  size?: number;
  strokeWidth?: number;
  color?: string;
  /** Fill used when saved. Defaults to a lighter tint of the stroke. */
  fillColor?: string;
  initialBookmarked?: boolean;
  onChange?: (next: boolean) => void;
  className?: string;
}) {
  const [bookmarked, setBookmarked] = React.useState(initialBookmarked);
  const notify = useToggleToast();

  return (
    <button
      type="button"
      aria-label={bookmarked ? "已收藏" : "收藏"}
      aria-pressed={bookmarked}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const next = !bookmarked;
        setBookmarked(next);
        onChange?.(next);
        notify.bookmark(next);
      }}
      className={`${className} rounded-sm outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[#259c73]/50`}
    >
      <Bookmark
        size={size}
        strokeWidth={strokeWidth}
        color={color}
        fill={bookmarked ? fillColor : "none"}
      />
    </button>
  );
}
